/**
 * Autoload singleton "Game" exposed to node scripts when running inside the
 * real bout (or editor play with bridge attached).
 *
 * Scripts use: Engine.get_singleton('Game') or get_tree().get_singleton via API.
 */

export interface GameBridgeState {
  mode: string;
  playerScore: number;
  opponentScore: number;
  timeLeft: number;
  playerX: number;
  opponentX: number;
  priority: string;
  boutMode: string;
}

export type GameBridgeHooks = {
  getState: () => GameBridgeState;
  /** Apply movement intent from a script (-1..1) for player or opponent */
  setMoveIntent?: (who: 'player' | 'opponent', move: number) => void;
  setActionIntent?: (
    who: 'player' | 'opponent',
    action: 'lunge' | 'fleche' | 'parry_high' | 'parry_low' | 'riposte',
  ) => void;
  showBanner?: (text: string) => void;
  playSfx?: (name: string) => void;
  setHint?: (text: string) => void;
  pauseGame?: () => void;
  getSpawn?: (name: string) => { x: number; y: number; z: number } | null;
};

/** Script-facing Game singleton object. */
export function createGameSingleton(hooks: GameBridgeHooks): Record<string, unknown> {
  return {
    get mode() {
      return hooks.getState().mode;
    },
    get player_score() {
      return hooks.getState().playerScore;
    },
    get opponent_score() {
      return hooks.getState().opponentScore;
    },
    get time_left() {
      return hooks.getState().timeLeft;
    },
    get player_x() {
      return hooks.getState().playerX;
    },
    get opponent_x() {
      return hooks.getState().opponentX;
    },
    get priority() {
      return hooks.getState().priority;
    },
    get bout_mode() {
      return hooks.getState().boutMode;
    },
    get_state() {
      return hooks.getState();
    },
    set_move(who: 'player' | 'opponent', move: number) {
      hooks.setMoveIntent?.(who, move);
    },
    action(who: 'player' | 'opponent', name: string) {
      hooks.setActionIntent?.(who, name as 'lunge' | 'fleche' | 'parry_high' | 'parry_low' | 'riposte');
    },
    banner(text: string) {
      hooks.showBanner?.(text);
    },
    sfx(name: string) {
      hooks.playSfx?.(name);
    },
    hint(text: string) {
      hooks.setHint?.(text);
    },
    pause() {
      hooks.pauseGame?.();
    },
    get_spawn(name: string) {
      return hooks.getSpawn?.(name) ?? null;
    },
  };
}
